import React, { useState, useRef, useEffect } from "react";
import TextInput from "../../common/Form/textInput";
import "./ChatSupport.css";

import chatbotIcon from "./chatbot-img.png";

const CHATBOT_ICON_IMAGE = chatbotIcon;

const ChatSupport = ({ getCurrentUser }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "...",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [username, setUsername] = useState(null);
  const messagesEndRef = useRef(null);
  const conversationRef = useRef(null);
  const abortControllerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!getCurrentUser) return;
    
    const fetchProfileData = async () => {
      try {
        const response = await getCurrentUser();
        if (!response) return;
        
        const fullUsername = response.user?.username || response.username;
        
        const extractedUsername = fullUsername ? fullUsername.split(' ')[0] : null;
        
        if (extractedUsername) {
          setUsername(extractedUsername);
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === 1
                ? {
                    ...msg,
                    text: `Hi ${extractedUsername}, I'm Convo, your virtual assistant. You can connect to live agent or I can help you below.`,
                  }
                : msg
            )
          );
        }

      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    fetchProfileData();
  }, [getCurrentUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, streamingMessageId]);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleSend = async () => {
    if (inputText.trim() === "") {
      return;
    }

    // Cancel any ongoing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Add user message
    const userMessage = {
      id: Date.now(),
      text: inputText.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    const userQuery = inputText.trim();
    setMessages((prev) => [...prev, userMessage]);
    setInputText("");

    // Show typing indicator (will be replaced by message when first token arrives)
    setIsTyping(true);

    // Create bot message ID for when first token arrives
    const botMessageId = Date.now() + 1;
    setStreamingMessageId(botMessageId);

    // Create new abort controller for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      // Call external chatbot API directly
      const chatbotUrl = 'https://rag-bot.solutions.iqvia.com/v1/stream';
      const res = await fetch(chatbotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ZGFG7WsCM4CURojf7tq165hK1RGsGrt7XoiZDex7w0RfL9bjoTzs7hq8Gdd8AF809UYcc7AcFZGT7aHKrIO2ifAhfPK6XWoqK9RpFTaHQtoQuS86pm19HLaIMJVhTJwhGTbQsFDTfG2TuDJa56XxWWCkjJWmhEjVmLFjGGBLmIPoHuYq9vhrW7htWCRdflSGT5FvoCQr6UwV2zKRKxiOOmRC5Go1dfcotGcX5gTOuOmtE8ZUZXEI0Y00we758xUSFJd2oAQuWiz0Yqmpxjgle8G7KwJ02kwXX2Y5WsP1LX0kVJpkksF2gMYSY2kHYAa9HKyzOFmzrVxRgVB'
        },
        body: JSON.stringify({
          query: userQuery,
          session_id: username || 'demo_4'
        }),
        signal
      });

      if (!res.ok) {
        // Try to read error response body
        let errorMessage = `HTTP error! status: ${res.status}`;
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
          console.error('Error response:', errorData);
        } catch (e) {
          const errorText = await res.text();
          if (errorText) {
            errorMessage = errorText;
            console.error('Error response text:', errorText);
          }
        }
        throw new Error(errorMessage);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const accumulatedResponseRef = { current: '' };
      let buffer = '';
      let currentEventType = null;
      let firstTokenReceived = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Process complete lines from the buffer
        const lines = buffer.split('\n');
        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          
          // Track the current event type
          if (trimmedLine.startsWith('event: ')) {
            currentEventType = trimmedLine.substring(7).trim(); // Extract event type after "event: "
            continue;
          }
          
          // Only extract data from "event: token" events
          if (trimmedLine.startsWith('data: ') && currentEventType === 'token') {
            const dataValue = line.substring(6); // Remove "data: " prefix (keep original line to preserve spacing)
            
            // Hide typing indicator and create message when first token arrives
            if (!firstTokenReceived) {
              setIsTyping(false);
              firstTokenReceived = true;
              
              // Create the bot message with first token
              const botMessage = {
                id: botMessageId,
                text: dataValue,
                sender: "bot",
                timestamp: new Date(),
                isStreaming: true,
              };
              setMessages((prev) => [...prev, botMessage]);
              accumulatedResponseRef.current = dataValue;
            } else {
              // Add the token data to accumulated response
              accumulatedResponseRef.current += dataValue;
              
              // Update the streaming message with accumulated response
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === botMessageId
                    ? { ...msg, text: accumulatedResponseRef.current, isStreaming: true }
                    : msg
                )
              );
            }
          }
          
          // Reset event type after processing a data line
          if (trimmedLine.startsWith('data: ')) {
            currentEventType = null;
          }
        }
      }

      // Process any remaining buffer content
      if (buffer.trim()) {
        const lines = buffer.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('event: ')) {
            currentEventType = trimmedLine.substring(7).trim();
          } else if (trimmedLine.startsWith('data: ') && currentEventType === 'token') {
            const dataValue = line.substring(6);
            if (!firstTokenReceived) {
              setIsTyping(false);
              firstTokenReceived = true;
              const botMessage = {
                id: botMessageId,
                text: dataValue,
                sender: "bot",
                timestamp: new Date(),
                isStreaming: true,
              };
              setMessages((prev) => [...prev, botMessage]);
              accumulatedResponseRef.current = dataValue;
            } else {
              accumulatedResponseRef.current += dataValue;
            }
          }
        }
      }

      // Mark message as complete with final text (only if message was created)
      if (firstTokenReceived) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? { ...msg, text: accumulatedResponseRef.current, isStreaming: false }
              : msg
          )
        );
      } else {
        // If no tokens received, show error message
        setIsTyping(false);
        const errorMessage = {
          id: botMessageId,
          text: 'No response received from the server.',
          sender: "bot",
          timestamp: new Date(),
          isStreaming: false,
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
      setStreamingMessageId(null);
      
      // Ensure typing indicator is hidden
      setIsTyping(false);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Stream aborted');
        setIsTyping(false);
      } else {
        console.error('Error fetching stream:', error);
        // Show the actual error message if available
        const errorText = error.message || 'Sorry, there was an error processing your request. Please try again.';
        
        // Update message with error
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  text: errorText,
                  isStreaming: false,
                }
              : msg
          )
        );
        setStreamingMessageId(null);
        setIsTyping(false);
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Button - Only show when modal is closed */}
      {!open && (
        <button
          onClick={handleOpen}
          className="chat-support-trigger"
        >
          <img 
            src={CHATBOT_ICON_IMAGE} 
            alt="Chatbot Icon" 
            className="chatbot-icon-trigger"
          />
        </button>
      )}

      {/* Chat Modal - Only show when modal is open */}
      {open && (
      <div className="chat-modal-overlay">
        <div className="MUI-modal-dialog">
          <div className="top" />
          
          {/* Title Section with Dashed Line */}
          <div className="title-section">
            <div className="title">
              Chat Support
            </div>
            <div className="title-dashed-line" />
          </div>

          {/* Conversation Area */}
          <div
            ref={conversationRef}
            className="conversation-area"
          >
            {messages.length === 0 ? (
              <div className="empty-message">
                Start a conversation with our support team...
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.sender} ${message.sender === "user" ? "message-user" : "message-bot"}`}
                >
                  <div className="message-content">
                    {message.sender === "bot" && (
                      <div className="message-icon">
                        <img 
                          src={CHATBOT_ICON_IMAGE} 
                          alt="Chatbot Icon" 
                          className="chatbot-icon-message"
                          style={{ width: "39px", height: "39px" }}
                        />
                      </div>
                    )}
                    <div className={`message-bubble ${message.sender === "user" ? "message-bubble-user" : "message-bubble-bot"}`}>
                      <div className="message-text">
                        {message.text}
                      </div>
                    </div>
                  </div>
                  <div className={`message-timestamp ${message.sender === "user" ? "message-timestamp-user" : "message-timestamp-bot"}`}>
                    {message.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))
            )}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="message bot message-typing">
                <div className="message-content">
                  <div className="message-icon">
                    <img 
                      src={CHATBOT_ICON_IMAGE} 
                      alt="Chatbot Icon" 
                      className="chatbot-icon-message"
                      style={{ width: "39px", height: "39px" }}
                    />
                  </div>
                  <div className="message-bubble typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="input-area">
            <TextInput
              field={{
                name: "message", 
                onBlur: () => {}, 
                onChange: () => {},
                onKeyDown: handleKeyDown
              }}
              meta={{ touched: false, error: "" }}
              placeholder="Please type your request"
              value={inputText}
              onChange={(value) => setInputText(value)}
              multiline={false}
              fullWidth={true}
              style={{
                fontFamily: '"Proxima Nova", Helvetica',
                fontSize: "14px",
                height: "50px !important"
              }}
            />
          </div>

          {/* Footer with Buttons */}
          <footer className="chat-footer">
            <button
              onClick={handleCancel}
              className="button"
            >
              Cancel
            </button>

            <button
              onClick={handleSend}
              className="label-wrapper"
            >
              Send
            </button>
          </footer>
        </div>
      </div>
      )}
    </>
  );
};

export default ChatSupport;

